import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { formatRupiah } from "../lib/format";
import { ChevronDown, ChevronUp, Search, AlertCircle, RefreshCw, FileText, Clock } from "lucide-react";
import { RekapBulananModal } from "../components/RekapBulananModal";
import { PreviewNotaModal } from "../components/PreviewNotaModal";
import { RejectMenungguPanel } from "../components/RejectMenungguPanel";
import { AsistenAIPanel } from "../components/AsistenAIPanel";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveLogo = (p) => (p && p.startsWith("/api/")) ? `${BACKEND}${p}` : (p || "");

const PERIODE_OPTS = [
    { v: "harian", l: "Harian" }, { v: "mingguan", l: "Mingguan" },
    { v: "bulanan", l: "Bulanan" }, { v: "custom", l: "Custom" },
];

export default function Dashboard() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [periode, setPeriode] = useState("bulanan");
    const [logoUrl, setLogoUrl] = useState("");
    const [namaPerusahaan, setNamaPerusahaan] = useState("");

    useEffect(() => {
        api.get("/pengaturan/public").then((r) => {
            setLogoUrl(r.data?.logo_url || "");
            setNamaPerusahaan(r.data?.nama_perusahaan || "");
        }).catch(() => {});
    }, []);
    const [dari, setDari] = useState("");
    const [sampai, setSampai] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [tab, setTab] = useState("bahan");
    const [q, setQ] = useState("");
    const [sort, setSort] = useState({ key: "hak_belum_dibayar", dir: -1 });
    const [showRekap, setShowRekap] = useState(false);
    const [preview, setPreview] = useState(null); // { title, url, filename }

    const load = async () => {
        setLoading(true); setErr("");
        try {
            const qs = new URLSearchParams();
            qs.set("periode", periode);
            if (dari) qs.set("tanggal_dari", dari);
            if (sampai) qs.set("tanggal_sampai", sampai);
            const r = await api.get(`/dashboard/master?${qs.toString()}`);
            setData(r.data);
        } catch (e) { setErr(e.response?.data?.detail || "Gagal memuat data"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [periode]);

    const perVendor = data?.saldo_kumulatif?.per_vendor || [];
    const filtered = q ? perVendor.filter((v) => v.nama.toLowerCase().includes(q.toLowerCase()) || (v.kode_vendor || "").toLowerCase().includes(q.toLowerCase())) : perVendor;
    const sorted = [...filtered].sort((a, b) => {
        const av = a[sort.key] ?? 0, bv = b[sort.key] ?? 0;
        return (av < bv ? -1 : av > bv ? 1 : 0) * sort.dir;
    });

    return (
        <div className="space-y-4 md:space-y-5">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    {logoUrl && (
                        <img
                            src={resolveLogo(logoUrl)}
                            alt="logo"
                            className="w-10 h-10 md:w-11 md:h-11 rounded-lg object-contain bg-white flex-shrink-0"
                            style={{ border: "1px solid var(--border-soft)" }}
                            data-testid="dash-header-logo"
                            onError={() => setLogoUrl("")}
                        />
                    )}
                    <div className="min-w-0">
                        <h1 className="text-xl md:text-2xl font-semibold tracking-tight" style={{ color: "var(--text-primary)", letterSpacing: "-0.3px" }}>
                            Dashboard Master
                        </h1>
                        {namaPerusahaan && (
                            <div className="text-[11px] md:text-xs uppercase tracking-wider font-medium truncate" style={{ color: "var(--text-muted)" }} data-testid="dash-nama-perusahaan">
                                {namaPerusahaan}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <select value={periode} onChange={(e) => setPeriode(e.target.value)} data-testid="dash-periode"
                            className="rounded-lg px-3 py-2 text-sm bg-white"
                            style={{ border: "1px solid var(--border)" }}>
                        {PERIODE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                    {periode === "custom" && (
                        <>
                            <input type="date" value={dari} onChange={(e) => setDari(e.target.value)} data-testid="dash-dari"
                                className="rounded-lg px-3 py-2 text-sm bg-white"
                                style={{ border: "1px solid var(--border)" }} />
                            <input type="date" value={sampai} onChange={(e) => setSampai(e.target.value)} data-testid="dash-sampai"
                                className="rounded-lg px-3 py-2 text-sm bg-white"
                                style={{ border: "1px solid var(--border)" }} />
                        </>
                    )}
                    <button onClick={load} data-testid="dash-apply"
                        className="px-4 py-2 text-white rounded-lg text-sm font-medium"
                        style={{ background: "var(--apple-blue)" }}>
                        Terapkan
                    </button>
                    <button onClick={() => setShowRekap(true)} data-testid="dash-cetak-rekap"
                            className="px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5"
                            style={{ border: "1px solid var(--apple-blue)", color: "var(--apple-blue)" }}>
                        <FileText className="w-3.5 h-3.5" /> Cetak Rekap
                    </button>
                </div>
            </div>

            {err && (
                <div className="panel border-red-200 bg-red-50 p-4 mb-4 flex items-center justify-between" data-testid="dash-error">
                    <div className="flex items-center gap-2 text-red-700 text-sm"><AlertCircle className="w-4 h-4" />{err}</div>
                    <button onClick={load} className="text-red-700 hover:text-red-900 flex items-center gap-1 text-sm font-medium"><RefreshCw className="w-3.5 h-3.5" /> Coba Lagi</button>
                </div>
            )}

            {/* 4 Kartu Ringkasan — grid 2 kolom di mobile, 4 di desktop */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3 my-4 md:my-5">
                <KartuRingkas label="Nilai Setoran Periode" sub="AKTIVITAS PERIODE"
                              nilai={data?.aktivitas_periode?.setor_total_nilai} loading={loading} testid="kpi-setor" />
                <KartuRingkas label="Bon Pengambilan Barang" sub="SALDO KUMULATIF"
                              nilai={data?.saldo_kumulatif?.total_bon_terbuka} loading={loading} testid="kpi-bon" />
                <KartuRingkas label="Estimasi Pembayaran" sub="BUKAN PASTI"
                              nilai={Math.max(0, data?.saldo_kumulatif?.total_estimasi_pembayaran || 0)} loading={loading} testid="kpi-estimasi" />
                <KartuRingkas label="Hutang Vendor Bersih" sub="DARI VENDOR"
                              nilai={-Math.min(0, data?.saldo_kumulatif?.total_estimasi_pembayaran || 0)} loading={loading} testid="kpi-hutang-vendor" />
            </div>

            {/* Panel Asisten AI (semua authenticated user, posisi sesudah KPI, sebelum Reject panel) */}
            {user && <AsistenAIPanel />}

            {/* Panel Pengingat Reject (semua jenis, dengan hari tunggu) */}
            <RejectMenungguPanel />

            {/* Info tambahan rijek */}
            {data?.aktivitas_periode?.reject_terbuka_di_periode > 0 && (
                <div className="panel border-amber-200 bg-amber-50 p-3 mb-6 text-xs text-amber-800" data-testid="kpi-reject">
                    <b>{data.aktivitas_periode.reject_terbuka_di_periode}</b> rijek A terbuka di periode ini — perlu tindak lanjut.
                </div>
            )}

            {/* Tabel per vendor — Desktop. Card list — Mobile */}
            <div className="rounded-xl bg-white overflow-hidden mb-4 md:mb-6" style={{ border: "1px solid var(--border)" }}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 md:p-4" style={{ borderBottom: "1px solid var(--border)" }}>
                    <h2 className="font-semibold text-sm md:text-base" style={{ color: "var(--text-primary)" }}>Ringkasan Per Vendor</h2>
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari vendor..." data-testid="dash-search-vendor"
                               className="pl-8 pr-3 py-2 text-sm rounded-lg w-full sm:w-56 bg-white"
                               style={{ border: "1px solid var(--border)" }} />
                    </div>
                </div>

                {/* MOBILE card list */}
                <div className="md:hidden p-3 space-y-2">
                    {loading && [1, 2, 3].map((i) => (
                        <div key={i} className="h-16 bg-slate-100 animate-pulse rounded-lg" />
                    ))}
                    {!loading && sorted.length === 0 && (
                        <div className="text-center py-8 text-sm italic" style={{ color: "var(--text-muted)" }}>
                            Belum ada vendor. Tambahkan vendor dulu di menu Data Input.
                        </div>
                    )}
                    {!loading && sorted.map((v) => {
                        const hak = Math.max(0, v.estimasi_pembayaran || 0);
                        const hutang = -Math.min(0, v.estimasi_pembayaran || 0);
                        return (
                            <div key={v.id} onClick={() => nav(`/vendor/${v.id}`)}
                                data-testid={`dash-card-${v.id}`}
                                className="rounded-lg p-3 cursor-pointer active:bg-[var(--bg-subtle)]"
                                style={{ border: "1px solid var(--border)" }}>
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-sm truncate" style={{ color: "var(--text-primary)" }}>{v.nama}</div>
                                        <div className="text-[11px] font-mono" style={{ color: "var(--text-muted)" }}>{v.kode_vendor}</div>
                                    </div>
                                    <span className="text-xs font-medium whitespace-nowrap" style={{ color: "var(--apple-blue)" }}>Buka →</span>
                                </div>
                                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                                    <div><span style={{ color: "var(--text-muted)" }}>Bon sisa</span></div>
                                    <div className="text-right num-tabular" style={{ color: "var(--text-primary)" }}>{formatRupiah(v.bon_terbuka)}</div>
                                    <div><span style={{ color: "var(--text-muted)" }}>Kasbon</span></div>
                                    <div className="text-right num-tabular" style={{ color: "var(--text-primary)" }}>{formatRupiah(v.kasbon_terbuka)}</div>
                                    {hak > 0 && (
                                        <>
                                            <div><span style={{ color: "var(--text-muted)" }}>Hak dibayar</span></div>
                                            <div className="text-right num-tabular font-semibold" style={{ color: "var(--apple-blue-hover)" }}>{formatRupiah(hak)}</div>
                                        </>
                                    )}
                                    {hutang > 0 && (
                                        <>
                                            <div><span style={{ color: "var(--text-muted)" }}>Hutang vendor</span></div>
                                            <div className="text-right num-tabular font-semibold" style={{ color: "var(--apple-red)" }}>{formatRupiah(hutang)}</div>
                                        </>
                                    )}
                                    {(v.reject_terbuka_pcs || 0) > 0 && (
                                        <>
                                            <div><span style={{ color: "var(--text-muted)" }}>Rijek</span></div>
                                            <div className="text-right num-tabular" style={{ color: "var(--warn)" }}>{v.reject_terbuka_pcs} pcs</div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* DESKTOP table */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="table-nota">
                        <thead>
                            <tr>
                                <HeaderSort label="Nama" k="nama" sort={sort} setSort={setSort} className="sticky left-0 bg-white z-10 border-r border-[var(--border)]" />
                                <th>Kode</th>
                                <th className="text-right">Bon Pengambilan Barang</th>
                                <th className="text-right">Kasbon Terbuka</th>
                                <HeaderSort label="Hak Belum Dibayar" k="estimasi_pembayaran" align="right" sort={sort} setSort={setSort} />
                                <th className="text-right">Hutang Vendor</th>
                                <th className="text-right">Rijek Pcs</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody data-testid="dash-tabel-vendor">
                            {loading && [1, 2, 3].map((i) => <SkeletonRow key={i} cols={8} />)}
                            {!loading && sorted.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-10 italic" style={{ color: "var(--text-muted)" }}>
                                    Belum ada vendor. Tambahkan vendor dulu di menu Data Input.
                                </td></tr>
                            )}
                            {!loading && sorted.map((v) => {
                                const hak = Math.max(0, v.estimasi_pembayaran || 0);
                                const hutang = -Math.min(0, v.estimasi_pembayaran || 0);
                                return (
                                    <tr key={v.id} onClick={() => nav(`/vendor/${v.id}`)} data-testid={`dash-row-${v.id}`}>
                                        <td className="font-medium sticky left-0 bg-white border-r border-[var(--border)]">{v.nama}</td>
                                        <td style={{ color: "var(--text-muted)" }} className="font-mono text-xs">{v.kode_vendor}</td>
                                        <td className="num">{formatRupiah(v.bon_terbuka)}</td>
                                        <td className="num">{formatRupiah(v.kasbon_terbuka)}</td>
                                        <td className="num num-kredit">{formatRupiah(hak)}</td>
                                        <td className="num num-debit">{formatRupiah(hutang)}</td>
                                        <td className="num">{v.reject_terbuka_pcs || 0}</td>
                                        <td className="text-right"><span className="text-xs font-medium" style={{ color: "var(--apple-blue)" }}>Buka Buku →</span></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Tabs laporan sekunder */}
            <div className="panel">
                <div className="flex gap-1 p-2 border-b border-[var(--border)]">
                    {[
                        ["bahan", "Laporan Bahan Keluar"],
                        ["ambil", "Pengambilan Kain"],
                        ["setor", "Setoran"],
                    ].map(([k, l]) => (
                        <button key={k} onClick={() => setTab(k)} data-testid={`dash-tab-${k}`}
                                className={`px-3 py-1.5 text-sm rounded ${tab === k ? "bg-teal-50 text-teal-700 font-medium" : "text-slate-600 hover:bg-slate-100"}`}>
                            {l}
                        </button>
                    ))}
                </div>
                <AktivitasTab tab={tab} dari={data?.tanggal_dari} sampai={data?.tanggal_sampai} />
            </div>

            <RekapBulananModal
                open={showRekap}
                onClose={() => setShowRekap(false)}
                onCetak={(bulan, tahun) => {
                    const BULAN = ["", "Januari", "Februari", "Maret", "April", "Mei", "Juni",
                                   "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
                    setShowRekap(false);
                    setPreview({
                        title: `Rekap Bulanan ${BULAN[bulan]} ${tahun}`,
                        url: `/laporan/rekap-bulanan/pdf?bulan=${bulan}&tahun=${tahun}`,
                        filename: `Rekap_Bulanan_${BULAN[bulan]}_${tahun}.pdf`,
                    });
                }}
            />
            <PreviewNotaModal
                open={!!preview}
                onClose={() => setPreview(null)}
                title={preview?.title || ""}
                url={preview?.url || ""}
                filename={preview?.filename || "rekap.pdf"}
            />
        </div>
    );
}

function KartuRingkas({ label, sub, nilai, loading, formatNum, testid }) {
    return (
        <div className="rounded-xl bg-white p-3 md:p-4" style={{ border: "1px solid var(--border)" }} data-testid={testid}>
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-1 truncate" style={{ color: "var(--text-muted)" }}>{label}</div>
            {loading ? (
                <div className="h-6 w-20 bg-slate-100 animate-pulse rounded my-1" />
            ) : (
                <div className="text-lg md:text-2xl font-semibold num-tabular tracking-tight" style={{ color: "var(--text-primary)", letterSpacing: "-0.3px" }}>
                    {formatNum ? formatNum(nilai) : formatRupiah(nilai || 0)}
                </div>
            )}
            <div className="text-[10px] mt-1 truncate" style={{ color: "var(--text-muted)" }}>{sub}</div>
        </div>
    );
}

function HeaderSort({ label, k, align, sort, setSort, className = "" }) {
    const dir = sort.key === k ? sort.dir : 0;
    return (
        <th className={`${align === "right" ? "text-right" : ""} ${className}`}>
            <button onClick={() => setSort({ key: k, dir: dir === -1 ? 1 : -1 })} className="inline-flex items-center gap-1 hover:text-slate-900">
                {label}
                {dir !== 0 && (dir === -1 ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
            </button>
        </th>
    );
}

function SkeletonRow({ cols }) {
    return <tr>{Array.from({ length: cols }).map((_, i) => <td key={i}><div className="h-4 bg-slate-200 animate-pulse rounded w-24" /></td>)}</tr>;
}

function AktivitasTab({ tab, dari, sampai }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(true);
        const url = tab === "setor" ? "/setor-barang" : "/bon-kain";
        api.get(url).then((r) => {
            let data = r.data || [];
            if (dari) data = data.filter((d) => (d.tanggal || d.tanggal_setor || "") >= dari);
            if (sampai) data = data.filter((d) => (d.tanggal || d.tanggal_setor || "") <= sampai);
            if (tab === "bahan") data = data.filter((d) => d.jenis_bon !== "rijek_B_buyback");
            setRows(data);
        }).catch(() => setRows([])).finally(() => setLoading(false));
    }, [tab, dari, sampai]);

    return (
        <div className="overflow-x-auto p-2">
            <table className="table-nota">
                <thead>
                    <tr>
                        <th>Tanggal</th>
                        <th>Nomor</th>
                        <th>Vendor</th>
                        <th>Petugas</th>
                        <th className="text-right">Nilai</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && [1, 2].map((i) => <SkeletonRow key={i} cols={5} />)}
                    {!loading && rows.length === 0 && (
                        <tr><td colSpan={5} className="text-center text-slate-400 py-6 italic">Belum ada transaksi dalam periode ini.</td></tr>
                    )}
                    {!loading && rows.map((r) => (
                        <tr key={r.id}>
                            <td>{formatDMY(r.tanggal || r.tanggal_setor)}</td>
                            <td className="font-mono text-xs">{r.nomor_bon || r.nomor_setor}</td>
                            <td>{r.nama_vendor_snapshot || "-"}</td>
                            <td>{r.petugas || "-"}</td>
                            <td className="num">{formatRupiah(r.total_nilai)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function formatDMY(iso) {
    if (!iso) return "-";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
}
